import type { SplitViewContentType } from '@saaga/shared/store/slices/split-view.slice'
import { type ReactNode, useCallback, useRef, useState } from 'react'

import { useIsMobile } from '@/hooks/use-mobile.hook'

import { ChatSplitLayoutDesktop } from './chat-split-layout-desktop.atom'
import { ChatSplitLayoutMobile } from './chat-split-layout-mobile.atom'

const MIN_PANEL_PERCENT = 30
const DEFAULT_CHAT_PERCENT = 60

export interface ChatSplitLayoutProps {
  children: ReactNode
  isSplitViewOpen: boolean
  contentType: SplitViewContentType
  contentData: Record<string, unknown> | null
  onCloseSplitView: () => void
  className?: string
}

export const ChatSplitLayout: React.FC<ChatSplitLayoutProps> = ({
  children,
  isSplitViewOpen,
  contentType,
  contentData,
  onCloseSplitView,
  className,
}): ReactNode => {
  const isMobile = useIsMobile()
  const [chatPercent, setChatPercent] = useState(DEFAULT_CHAT_PERCENT)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleResize = useCallback((deltaX: number) => {
    if (!containerRef.current) return
    const containerWidth = containerRef.current.offsetWidth
    const deltaPercent = (deltaX / containerWidth) * 100
    setChatPercent((prev) =>
      Math.max(MIN_PANEL_PERCENT, Math.min(100 - MIN_PANEL_PERCENT, prev + deltaPercent))
    )
  }, [])

  if (isMobile) {
    return (
      <ChatSplitLayoutMobile
        isOpen={isSplitViewOpen}
        contentType={contentType}
        contentData={contentData}
        onClose={onCloseSplitView}
        className={className}
      >
        {children}
      </ChatSplitLayoutMobile>
    )
  }

  return (
    <ChatSplitLayoutDesktop
      isOpen={isSplitViewOpen}
      contentType={contentType}
      contentData={contentData}
      onClose={onCloseSplitView}
      className={className}
      containerRef={containerRef}
      chatPercent={chatPercent}
      onResize={handleResize}
    >
      {children}
    </ChatSplitLayoutDesktop>
  )
}
